import { PrimaryButton, SecondaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'
import Modal from 'styled-react-modal'

import useLegalAgreementsApi from '../../../../hooks/useLegalAgreementsApi'
import { useAuth } from '../../../../lib/auth'
import { logAndCaptureException } from '../../../../utils/errorTools'
import { userClaimIds } from '../../../../utils/userClaims'
import CoveredEntityDescription from '../../../global/agreements/covered-entity/CoveredEntityDescription'
import CoveredEntityRadioButtons from '../../../global/agreements/covered-entity/CoveredEntityRadioButtons'
import { CoveredEntityFormFieldValues } from '../../../global/agreements/covered-entity/types'

const StyledModal = Modal.styled({
  width: '95%',
  height: '95%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  backgroundColor: 'white',
  padding: '20px',
  '@media(min-width: 576px)': {
    width: '90%',
    height: '90%',
    padding: '40px 40px 20px',
  },
  '@media(min-width: 768px)': {
    width: '55%',
    height: 'auto',
  },
  '@media print': {
    width: '90%',
    height: 'auto',
  },
  '.modal-actions': {
    marginTop: '20px',
  },
  '.modal-actions-bottom': {
    display: 'flex',
    justifyContent: 'space-between',
    '@media print': {
      display: 'none',
    },
  },
  '.button-group': {
    display: 'flex',
    'button ~ button': {
      marginLeft: '24px',
    },
  },
  [PrimaryButton]: {
    minWidth: '6em',
  },
})

const StyledFormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  p {
    line-height: 1.43;
  }
`

const StyledScrollContainer = styled.form`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
`

const StyledFormTitle = styled.h2`
  margin-block-start: 0.67em;
  margin-block-end: 0.67em;
`

function formValueFromBoolean(value: boolean | undefined | null): 'yes' | 'no' | '' {
  if (value === true) {
    return 'yes'
  }
  if (value === false) {
    return 'no'
  }
  return ''
}

interface CoveredEntityModalProps {
  isOpen: boolean
  toggleModal: () => void
}

const CoveredEntityModal: React.FC<CoveredEntityModalProps> = ({ isOpen, toggleModal }) => {
  const { user } = useAuth()
  const { loading: apiLoading, onCoveredEntityUpdatedAsync } = useLegalAgreementsApi()
  const [userCoveredEntity, setUserCoveredEntity] = useState<boolean | null>(null)

  useEffect(() => {
    if (!user) {
      setUserCoveredEntity(null)
      return
    }
    const claimValue = user[userClaimIds.hipaaCoveredEntity]
    if (typeof claimValue !== 'boolean') {
      setUserCoveredEntity(null)
      return
    }
    setUserCoveredEntity(claimValue)
  }, [user])

  const { control, errors, formState, handleSubmit, setValue, watch } =
    useForm<CoveredEntityFormFieldValues>({
      defaultValues: {
        hipaaCoveredEntity: formValueFromBoolean(userCoveredEntity),
      },
      mode: 'onChange',
      // prevent form from losing values on re-render
      shouldUnregister: false,
    })

  useEffect(() => {
    setValue('hipaaCoveredEntity', formValueFromBoolean(userCoveredEntity))
  }, [setValue, userCoveredEntity])

  const onSubmit: SubmitHandler<CoveredEntityFormFieldValues> = useCallback(
    async (data) => {
      const { hipaaCoveredEntity } = data
      const newValue = hipaaCoveredEntity === 'yes'

      const saveIfChangedPromise: Promise<unknown> =
        userCoveredEntity === newValue
          ? Promise.resolve()
          : onCoveredEntityUpdatedAsync({ hipaaCoveredEntity: newValue })

      return saveIfChangedPromise
        .then(() => {
          toggleModal()
        })
        .catch((err) => {
          logAndCaptureException(err)
        })
    },
    [onCoveredEntityUpdatedAsync, userCoveredEntity, toggleModal],
  )

  const hipaaCoveredEntityValue = watch('hipaaCoveredEntity')

  const loading = formState.isSubmitting || apiLoading

  return (
    <StyledModal isOpen={isOpen} onBackgroundClick={toggleModal} onEscapeKeydown={toggleModal}>
      <StyledFormContainer noValidate onSubmit={handleSubmit(onSubmit)}>
        <StyledScrollContainer>
          <StyledFormTitle>Are you a Covered Entity under HIPAA?</StyledFormTitle>
          <CoveredEntityDescription />
          <CoveredEntityRadioButtons
            control={control}
            errors={errors}
            disabled={loading}
            dataProviderError={undefined}
          />
          {hipaaCoveredEntityValue === 'yes' && (
            <p>
              By selecting Yes, please note you will be required to agree to the terms of our
              Business Associate Agreement.
            </p>
          )}
        </StyledScrollContainer>
        <div className="modal-actions">
          <div className="modal-actions-bottom">
            <div className="button-group">
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
              </PrimaryButton>
              <SecondaryButton onClick={toggleModal} text="Close" disabled={loading} />
            </div>
          </div>
        </div>
      </StyledFormContainer>
    </StyledModal>
  )
}

export default CoveredEntityModal
